import styled, { keyframes } from "styled-components";
import { COLORS, THEME_FALLBACKS } from "../common/colors";

// ─── Animations ────────────────────────────────────────────────────────────────

export const rotate = keyframes`
  to { transform: rotate(1turn); }
`;

export const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const popupFadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95) translateY(10px); }
  to   { opacity: 1; transform: scale(1)    translateY(0); }
`;

// ─── Layout ────────────────────────────────────────────────────────────────────

export const Section = styled.section`
  position: relative;
  background-image: linear-gradient(
    ${({ theme }) => theme.lightBlueShade},
    ${({ theme }) => theme.yellowShade}
  );
  margin-top: 21px;
`;

Section.displayName = "Section";

export const Container = styled.div`
  width: 85%;
  min-height: 80vh;
  margin: -21px auto 0;
  display: flex;
  flex-direction: row-reverse;
  justify-content: center;
  align-items: center;

  /* Respect reduced-motion preference */
  @media (prefers-reduced-motion: no-preference) {
    animation: ${fadeInUp} 0.6s ease both;
  }

  @media (max-width: 64em) {
    width: 85%;
    min-height: 70vh;
  }

  @media (max-width: 48em) {
    flex-direction: column-reverse;
    min-height: 60vh;
    width: 100%;
    /* column-reverse flips visual order, so top spacing must live on the
       container itself rather than on ":first-child" (which is only the
       DOM-first element, not the visually-first one). */
    padding-top: 2.5rem;

    & > * {
      width: 100%;
    }

    /* Gap between the (visually top) image and the (visually bottom) text */
    & > *:first-child {
      margin-top: 2rem;
    }
  }

  @media (max-width: 30em) {
    min-height: 50vh;
  }
`;

Container.displayName = "Container";

export const Box = styled.div`
  width: 70%;
  height: 100%;
  display: flex;
  position: relative;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

Box.displayName = "Box";

// ─── Rotating Badge ────────────────────────────────────────────────────────────

export const Round = styled.div`
  position: absolute;
  bottom: 4rem;
  right: 90%;
  width: 6rem;
  height: 6rem;
  border: 1px solid ${({ theme }) => theme.buttonColor};
  border-radius: 50%;

  img {
    width: 100%;
    height: auto;

    @media (prefers-reduced-motion: no-preference) {
      animation: ${rotate} 6s linear infinite reverse;
    }
  }

  @media (max-width: 64em) {
    width: 4rem;
    height: 4rem;
    right: 2rem;
    bottom: 100%;
    display: none;
  }

  @media (max-width: 48em) {
    display: none;
  }
`;

Round.displayName = "Round";

export const ScrollButton = styled.button`
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: ${({ theme }) => theme.buttonColor};
  color: ${({ theme }) => theme.buttoncolorO};
  font-size: ${({ theme }) => theme.fontlg};
  transition: opacity 0.2s ease;

  &:hover { opacity: 0.85; }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.buttonColor};
    outline-offset: 3px;
  }

  @media (max-width: 64em) {
    width: 1.5rem;
    height: 1.5rem;
    font-size: ${({ theme }) => theme.fontmd};
  }
`;

ScrollButton.displayName = "ScrollButton";

// ─── Hero Image ────────────────────────────────────────────────────────────────

export const HomeImages = styled.img`
  width: 75%;
  height: auto;
  object-fit: cover;
  margin-top: 4%;
  display: block;
`;

HomeImages.displayName = "HomeImages";

// ─── Wave Divider ──────────────────────────────────────────────────────────────
/*
 * Wave shape is built with two pseudo-elements.
 * Magic numbers explained:
 *   height: 300px  — tall enough for the ellipse curve to look smooth
 *   top: 20px      — ::before offset pushes white cap slightly below the band
 *   top: -240px    — ::after pulls the yellow ellipse up to mask the overlap
 *   -25% / left/right — shifts each ellipse so only one side of the curve shows
 */
export const Wave = styled.div`
  display: block;
  position: relative;
  height: 40px;
  width: 100%;
  background-color: ${({ theme }) => theme.yellowShade};
  transform: scale(1, 1.5);

  &::before {
    content: "";
    display: block;
    position: absolute;
    border-radius: 100%;
    width: 100%;
    height: 300px;
    background-color: white;
    right: -25%;
    top: 20px;
  }

  &::after {
    content: "";
    display: block;
    position: absolute;
    border-radius: 100%;
    width: 100%;
    height: 300px;
    background-color: ${({ theme }) => theme.yellowShade};
    left: -25%;
    top: -240px;
    clip-path: ellipse(100% 15% at -15% 100%);
  }
`;

Wave.displayName = "Wave";